import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import "../Css/ChatBot.css";
import { getResponse, getWelcomeMessage } from "./ChatbotEngine";

const STORAGE_KEY = "sfe-chatbot-history";

// Inline icons keep the component dependency-free
const Icon = {
  Chat: (props) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M20 11.5c0 4.142-3.582 7.5-8 7.5-.728 0-1.434-.092-2.105-.265L4 21l1.5-3.95C4.55 15.835 4 14.224 4 12.5 4 8.358 7.582 5 12 5s8 3.358 8 6.5z" />
      <circle cx="8.5" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="15.5" cy="12" r="1" fill="currentColor" stroke="none" />
    </svg>
  ),
  Close: (props) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  ),
  Send: (props) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" />
    </svg>
  ),
  Trash: (props) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  ),
  Paddle: (props) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="13" cy="9" r="6.5" />
      <path d="M8.5 13.5L3 19l2 2 5.5-5.5" />
      <circle cx="13" cy="9" r="2.5" fill="currentColor" stroke="none" opacity="0.35" />
    </svg>
  ),
};

// Render text with **bold** and [label](url) and bare URLs as React elements
const formatText = (text) => {
  const lines = text.split("\n");
  return lines.map((line, lineIdx) => {
    const parts = [];
    const regex = /(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)]+\))|(https?:\/\/[^\s]+)|(\b[\w.+-]+@[\w-]+\.[\w.-]+\b)/g;
    let lastIdx = 0;
    let match;
    let key = 0;
    while ((match = regex.exec(line)) !== null) {
      if (match.index > lastIdx) {
        parts.push(<span key={key++}>{line.slice(lastIdx, match.index)}</span>);
      }
      if (match[1]) {
        parts.push(<strong key={key++}>{match[1].slice(2, -2)}</strong>);
      } else if (match[2]) {
        const m = match[2].match(/\[([^\]]+)\]\(([^)]+)\)/);
        if (m) {
          parts.push(
            <a key={key++} href={m[2]} target="_blank" rel="noopener noreferrer">{m[1]}</a>
          );
        }
      } else if (match[3]) {
        parts.push(
          <a key={key++} href={match[3]} target="_blank" rel="noopener noreferrer">{match[3]}</a>
        );
      } else if (match[4]) {
        parts.push(
          <a key={key++} href={`mailto:${match[4]}`}>{match[4]}</a>
        );
      }
      lastIdx = regex.lastIndex;
    }
    if (lastIdx < line.length) {
      parts.push(<span key={key++}>{line.slice(lastIdx)}</span>);
    }
    return (
      <React.Fragment key={lineIdx}>
        {parts.length > 0 ? parts : <span>&nbsp;</span>}
        {lineIdx < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
};

const ChatBot = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [unread, setUnread] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Restore history on mount, or seed with a welcome message
  useEffect(() => {
    let restored = null;
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) restored = JSON.parse(saved);
    } catch (e) {
      /* ignore */
    }
    if (restored && Array.isArray(restored) && restored.length > 0) {
      setMessages(restored);
    } else {
      const w = getWelcomeMessage(location.pathname);
      setMessages([
        {
          id: Date.now(),
          role: "bot",
          text: w.text,
          suggestions: w.suggestions,
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch (e) {
      /* ignore */
    }
  }, [messages]);

  // Auto-scroll when new messages arrive
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages, isTyping, isOpen]);

  // Focus input + clear unread when opened
  useEffect(() => {
    if (isOpen) {
      setUnread(false);
      const t = setTimeout(() => inputRef.current?.focus(), 320);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // ESC closes
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && isOpen) setIsOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen]);

  const sendMessage = useCallback(
    (text) => {
      const trimmed = text.trim();
      if (!trimmed || isTyping) return;

      const userMsg = {
        id: Date.now(),
        role: "user",
        text: trimmed,
      };
      setMessages((prev) => [...prev, userMsg]);
      setInput("");
      setIsTyping(true);

      // Small artificial delay for a more natural feel
      const delay = 500 + Math.random() * 400;
      setTimeout(() => {
        const resp = getResponse(trimmed);
        const botMsg = {
          id: Date.now() + 1,
          role: "bot",
          text: resp.text,
          suggestions: resp.suggestions || [],
        };
        setMessages((prev) => [...prev, botMsg]);
        setIsTyping(false);
        setUnread((prevUnread) => (isOpen ? prevUnread : true));
      }, delay);
    },
    [isOpen, isTyping]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleClear = () => {
    const w = getWelcomeMessage(location.pathname);
    const fresh = [
      {
        id: Date.now(),
        role: "bot",
        text: w.text,
        suggestions: w.suggestions,
      },
    ];
    setMessages(fresh);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
  };

  return (
    <>
      <button
        type="button"
        className={`chatbot-fab ${isOpen ? "chatbot-fab--open" : ""}`}
        onClick={() => setIsOpen((v) => !v)}
        aria-label={isOpen ? "Close chat" : "Open chat assistant"}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <Icon.Close />
        ) : (
          <>
            <Icon.Chat />
            <span className="chatbot-fab__label">Let's Chat!</span>
            {unread && <span className="chatbot-fab__unread" aria-hidden="true" />}
          </>
        )}
      </button>

      <div
        className={`chatbot-panel ${isOpen ? "chatbot-panel--open" : ""}`}
        role="dialog"
        aria-label="SportsForEquity chat assistant"
        aria-hidden={!isOpen}
      >
        <div className="chatbot-header">
          <div className="chatbot-header__avatar" aria-hidden="true">
            <Icon.Paddle />
          </div>
          <div className="chatbot-header__text">
            <div className="chatbot-header__title">SportsForEquity Assistant</div>
            <div className="chatbot-header__status">
              <span className="chatbot-header__dot" aria-hidden="true" />
              Online — here to help
            </div>
          </div>
          <button
            type="button"
            className="chatbot-header__icon-btn"
            onClick={handleClear}
            aria-label="Clear conversation"
            title="Clear conversation"
          >
            <Icon.Trash />
          </button>
          <button
            type="button"
            className="chatbot-header__icon-btn"
            onClick={() => setIsOpen(false)}
            aria-label="Close chat"
            title="Close"
          >
            <Icon.Close />
          </button>
        </div>

        <div className="chatbot-messages" role="log" aria-live="polite" aria-relevant="additions">
          {messages.map((msg) => (
            <div key={msg.id} className={`chatbot-msg chatbot-msg--${msg.role}`}>
              {msg.role === "bot" && (
                <div className="chatbot-msg__avatar" aria-hidden="true">
                  <Icon.Paddle />
                </div>
              )}
              <div className="chatbot-msg__body">
                <div className="chatbot-msg__bubble">{formatText(msg.text)}</div>
                {msg.role === "bot" && msg.suggestions && msg.suggestions.length > 0 && (
                  <div className="chatbot-suggestions">
                    {msg.suggestions.map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        className="chatbot-suggestion"
                        onClick={() => sendMessage(s)}
                        disabled={isTyping}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="chatbot-msg chatbot-msg--bot">
              <div className="chatbot-msg__avatar" aria-hidden="true">
                <Icon.Paddle />
              </div>
              <div className="chatbot-msg__body">
                <div className="chatbot-msg__bubble chatbot-msg__bubble--typing" aria-label="Assistant is typing">
                  <span /><span /><span />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form className="chatbot-input" onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about programs, chapters, events…"
            aria-label="Type your message"
            disabled={isTyping}
            maxLength={500}
            autoComplete="off"
          />
          <button
            type="submit"
            aria-label="Send message"
            disabled={!input.trim() || isTyping}
          >
            <Icon.Send />
          </button>
        </form>

        <div className="chatbot-footer">
          Answers based on SportsForEquity content.{" "}
          For specific inquiries: <a href="mailto:info@sportsforequity.live">info@sportsforequity.live</a>
        </div>
      </div>
    </>
  );
};

export default ChatBot;